import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * Contact form. Posts JSON to /api/contact (TanStack Start server route
 * which forwards to Resend). Native React state — no react-hook-form.
 *
 * Anti-spam:
 * - Honeypot field "website" rendered off-screen with tabIndex={-1} +
 *   autoComplete="off". Bots that crawl-and-fill submit a non-empty
 *   value; the server returns 200 silently without sending mail.
 * - The 20-char minimum message length blocks one-shot drive-by spam.
 *
 * Accessibility:
 * - Every input has a Label tied via htmlFor/id.
 * - aria-required, aria-invalid, aria-describedby on each field.
 * - On submit error the first invalid field is focused.
 * - Error and success regions use aria-live so screen readers announce.
 */

const CATEGORIES = [
  "General question or feedback",
  "Bug report",
  "Feature request",
  "Barcode format correction",
  "SEO or content correction",
  "Business inquiry",
  "Privacy request (GDPR / CCPA)",
  "Legal notice (DMCA / trademark)",
  "Other",
] as const;
type Category = (typeof CATEGORIES)[number];

const URL_CATEGORIES = new Set<Category>(["Bug report", "SEO or content correction"]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_IN_NAME_RE = /(?:https?:\/\/|www\.)/i;

const MESSAGE_MAX = 5000;
const MESSAGE_MIN = 20;
const NAME_MAX = 100;
const EMAIL_MAX = 254;

type Status = "idle" | "submitting" | "success" | "error";

type FieldErrors = Partial<Record<"name" | "email" | "subject" | "url" | "message", string>>;

/** Map a free-text ?subject= query param to a canonical category. Tries
 *  exact match first, then case-insensitive partial match so old mailto
 *  links like ?subject=Bug%20report and ?subject=Privacy%20request still
 *  pre-select the right option. */
function categoryFromQueryParam(raw: string | null): Category | null {
  if (!raw) return null;
  const exact = (CATEGORIES as readonly string[]).find((c) => c === raw);
  if (exact) return exact as Category;
  const needle = raw.trim().toLowerCase();
  if (!needle) return null;
  return (
    ((CATEGORIES as readonly string[]).find((c) => c.toLowerCase().includes(needle)) as
      | Category
      | undefined) ?? null
  );
}

function validate(values: {
  name: string;
  email: string;
  subject: string;
  message: string;
  url: string;
}): FieldErrors {
  const errors: FieldErrors = {};
  const name = values.name.trim();
  if (!name) errors.name = "Required.";
  else if (name.length > NAME_MAX) errors.name = `Must be ${NAME_MAX} characters or fewer.`;
  else if (URL_IN_NAME_RE.test(name)) errors.name = "Name cannot contain a URL.";

  const email = values.email.trim();
  if (!email) errors.email = "Required.";
  else if (email.length > EMAIL_MAX) errors.email = `Must be ${EMAIL_MAX} characters or fewer.`;
  else if (!EMAIL_RE.test(email)) errors.email = "Enter a valid email address.";

  if (!values.subject) errors.subject = "Select a category.";
  else if (!(CATEGORIES as readonly string[]).includes(values.subject))
    errors.subject = "Select a valid category.";

  const message = values.message.trim();
  if (!message) errors.message = "Required.";
  else if (message.length < MESSAGE_MIN)
    errors.message = `Add at least ${MESSAGE_MIN} characters so we can help.`;
  else if (message.length > MESSAGE_MAX)
    errors.message = `Must be ${MESSAGE_MAX} characters or fewer.`;

  if (values.url) {
    try {
      new URL(values.url);
    } catch {
      errors.url = "Enter a full URL including https://.";
    }
  }

  return errors;
}

export function ContactForm() {
  const formId = useId();
  const id = (suffix: string) => `${formId}-${suffix}`;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState<Category | "">("");
  const [url, setUrl] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>("idle");
  const [topError, setTopError] = useState<string | null>(null);

  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  // The Radix select doesn't expose a focusable ref directly; we focus
  // its trigger via the id selector below if subject is the first error.
  const urlRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  // Prefill subject from ?subject=…
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const match = categoryFromQueryParam(params.get("subject"));
    if (match) setSubject(match);
  }, []);

  const showUrl = subject !== "" && URL_CATEGORIES.has(subject as Category);
  const messageChars = useMemo(() => message.length, [message]);
  const messageNearLimit = messageChars > MESSAGE_MAX - 200;

  function focusFirstError(errs: FieldErrors) {
    if (errs.name) nameRef.current?.focus();
    else if (errs.email) emailRef.current?.focus();
    else if (errs.subject) (document.getElementById(id("subject")) as HTMLElement | null)?.focus();
    else if (errs.url) urlRef.current?.focus();
    else if (errs.message) messageRef.current?.focus();
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTopError(null);
    const errs = validate({ name, email, subject, message, url });
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setStatus("error");
      focusFirstError(errs);
      return;
    }
    setErrors({});
    setStatus("submitting");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject,
          message: message.trim(),
          url: url.trim() || undefined,
          website, // honeypot — server short-circuits if non-empty
        }),
      });

      if (res.ok) {
        setStatus("success");
        return;
      }

      // Server returned an error. Try to surface the field-level details.
      let data: unknown = null;
      try {
        data = await res.json();
      } catch {
        /* non-JSON body */
      }
      if (
        data &&
        typeof data === "object" &&
        "details" in data &&
        typeof (data as { details: unknown }).details === "object" &&
        (data as { details: object }).details !== null
      ) {
        const details = (data as { details: Record<string, string> }).details;
        setErrors(details as FieldErrors);
        setTopError("Please correct the highlighted fields and try again.");
        focusFirstError(details as FieldErrors);
      } else {
        const baseMessage =
          (data && typeof data === "object" && "error" in data && typeof data.error === "string"
            ? data.error
            : null) ??
          "Sorry — we couldn't send your message. Please try again, or email hello@readbarcode.com directly.";
        // If the server included a diagnostic code/detail, append it so
        // the operator can triage without scraping Cloudflare logs.
        const code =
          data && typeof data === "object" && "code" in data && typeof data.code === "string"
            ? data.code
            : null;
        const detail =
          data && typeof data === "object" && "detail" in data && typeof data.detail === "string"
            ? data.detail
            : null;
        const suffix = code ? ` [${code}${detail ? `: ${detail}` : ""}]` : "";
        setTopError(`${baseMessage}${suffix}`);
      }
      setStatus("error");
    } catch (err) {
      console.error("[ContactForm] submit failure:", err);
      setTopError(
        "We couldn't reach the server. Check your connection and try again, or email hello@readbarcode.com directly.",
      );
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-6"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-foreground">Message sent — thank you.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              We&apos;ve received your message and will respond as soon as we can. For
              time-sensitive privacy requests we aim to respond within 30 days as required by GDPR.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      className="space-y-5 rounded-lg border border-border bg-background p-5 sm:p-6"
      aria-label="Contact form"
    >
      {topError && (
        <div
          role="alert"
          aria-live="assertive"
          className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
          <p className="text-sm text-destructive">{topError}</p>
        </div>
      )}

      {/* Honeypot — visually hidden, never tabbed into. */}
      <div aria-hidden style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <label htmlFor={id("website")}>Leave this field empty</label>
        <input
          id={id("website")}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field id={id("name")} label="Your name" required error={errors.name}>
          <Input
            id={id("name")}
            ref={nameRef}
            name="name"
            type="text"
            autoComplete="name"
            maxLength={NAME_MAX}
            required
            aria-required="true"
            aria-invalid={errors.name ? "true" : undefined}
            aria-describedby={errors.name ? id("name-error") : undefined}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field
          id={id("email")}
          label="Your email"
          required
          error={errors.email}
          hint="So we can reply."
        >
          <Input
            id={id("email")}
            ref={emailRef}
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            maxLength={EMAIL_MAX}
            required
            aria-required="true"
            aria-invalid={errors.email ? "true" : undefined}
            aria-describedby={errors.email ? id("email-error") : id("email-hint")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
      </div>

      <Field id={id("subject")} label="What is this about?" required error={errors.subject}>
        {/* Native <select> — this low-interactivity page doesn't need radix
            Select's floating-ui/popper machinery, which was the main source
            of main-thread work (TBT) on /contact. Native is lighter, keyboard-
            and screen-reader-accessible, and the better mobile picker. */}
        <select
          id={id("subject")}
          value={subject}
          onChange={(e) => setSubject(e.target.value as Category)}
          aria-required="true"
          aria-invalid={errors.subject ? "true" : undefined}
          aria-describedby={errors.subject ? id("subject-error") : undefined}
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
        >
          <option value="" disabled>
            Select a category
          </option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>

      {showUrl && (
        <Field
          id={id("url")}
          label="Relevant URL"
          error={errors.url}
          hint="Optional — the tool, guide or barcode page you're referring to."
        >
          <Input
            id={id("url")}
            ref={urlRef}
            name="url"
            type="url"
            inputMode="url"
            autoComplete="url"
            placeholder="https://readbarcode.com/…"
            aria-invalid={errors.url ? "true" : undefined}
            aria-describedby={errors.url ? id("url-error") : id("url-hint")}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </Field>
      )}

      <Field
        id={id("message")}
        label="Message"
        required
        error={errors.message}
        hint={
          <span
            className={
              messageNearLimit
                ? "tabular-nums text-amber-600"
                : "tabular-nums text-muted-foreground"
            }
          >
            {messageChars} / {MESSAGE_MAX}
          </span>
        }
      >
        <Textarea
          id={id("message")}
          ref={messageRef}
          name="message"
          rows={6}
          required
          maxLength={MESSAGE_MAX}
          aria-required="true"
          aria-invalid={errors.message ? "true" : undefined}
          aria-describedby={errors.message ? id("message-error") : id("message-hint")}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Add enough detail that we can help — the URL, the barcode value, what you expected and what happened."
        />
      </Field>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button type="submit" disabled={status === "submitting"} className="w-full sm:w-auto">
          <Send className="h-4 w-4" aria-hidden />
          {status === "submitting" ? "Sending…" : "Send message"}
        </Button>
        <p className="text-xs text-muted-foreground sm:max-w-sm sm:text-right">
          By submitting, you agree to our{" "}
          <Link to="/privacy" className="font-medium text-primary underline underline-offset-4">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link to="/terms" className="font-medium text-primary underline underline-offset-4">
            Terms of Use
          </Link>
          . We will only use your information to respond to your inquiry.
        </p>
      </div>
    </form>
  );
}

/** Common label + hint + error scaffolding so the form body stays focused on the inputs. */
function Field({
  id,
  label,
  required,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: React.ReactNode;
  error?: string;
  children: React.ReactNode;
}) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  return (
    <div>
      <Label htmlFor={id}>
        {label}
        {required && (
          <>
            {" "}
            <span aria-hidden className="text-destructive">
              *
            </span>
          </>
        )}
      </Label>
      <div className="mt-1.5">{children}</div>
      {!error && hint && (
        <p id={hintId} className="mt-1.5 text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1.5 text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
